import React, {useCallback, useEffect, useState} from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    IconButton,
    InputAdornment,
    MenuItem,
    Pagination,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import {Link, useNavigate} from 'react-router-dom';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import {getUser} from '../helpers/crypto';
import {Constants} from '../components/Constants';

type UserStatus = 'REGULAR' | 'OPT' | 'SUPEROPT';

interface UserRow {
    id: number;
    name: string;
    last_name: string;
    email: string;
    phone: string;
    post_address: string;
    status: UserStatus;
}

interface EditState {
    status: UserStatus;
    phone: string;
    post_address: string;
}

const STATUS_COLORS: Record<UserStatus, 'default' | 'primary' | 'warning'> = {
    REGULAR:  'default',
    OPT:      'primary',
    SUPEROPT: 'warning',
};

const AdminUsersPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [users, setUsers]     = useState<UserRow[]>([]);
    const [page, setPage]       = useState(1);
    const [pages, setPages]     = useState(1);
    const [total, setTotal]     = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch]   = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [editId, setEditId]   = useState<number | null>(null);
    const [editData, setEditData] = useState<EditState>({ status: 'REGULAR', phone: '', post_address: '' });
    const [saving, setSaving]   = useState(false);
    const [alert, setAlert]     = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

    useEffect(() => {
        const u = getUser();
        if (!u) { navigate('/login'); return; }
        if (u.status !== 'ADMIN') { navigate('/'); return; }
    }, [navigate]);

    const fetchUsers = useCallback(async (p: number, s: string) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: String(p) });
            if (s.trim()) params.set('search', s.trim());
            const res  = await fetch(`${Constants.SERVER_URL}${Constants.GET_USERS_PREFIX}?${params}`);
            const data = await res.json();
            if (data.success) {
                setUsers(data.users);
                setPages(data.pages);
                setTotal(data.total);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void fetchUsers(page, search); }, [page, search, fetchUsers]);

    const startEdit = (user: UserRow) => {
        setEditId(user.id);
        setEditData({ status: user.status, phone: user.phone || '', post_address: user.post_address || '' });
        setAlert(null);
    };

    const cancelEdit = () => { setEditId(null); };

    const saveEdit = async (userId: number) => {
        setSaving(true);
        setAlert(null);
        try {
            const res  = await fetch(`${Constants.SERVER_URL}${Constants.UPDATE_USER_PREFIX}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: userId, ...editData }),
            });
            const data = await res.json();
            if (data.success) {
                setUsers(prev => prev.map(u => u.id === userId
                    ? { ...u, status: editData.status, phone: editData.phone, post_address: editData.post_address }
                    : u
                ));
                setEditId(null);
                setAlert({ type: 'success', msg: 'Збережено' });
                setTimeout(() => setAlert(null), 3000);
            } else {
                setAlert({ type: 'error', msg: data.error || 'Помилка збереження' });
            }
        } catch {
            setAlert({ type: 'error', msg: 'Помилка з\'єднання' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ bgcolor: '#f7f9fc', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', borderBottom: '1px solid #e0e6ed'
                , position: 'sticky', top: 0, zIndex: 100 }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', alignItems: 'center', height: 70, gap: 2 }}>
                        <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}>
                            <LocalHospitalIcon sx={{ color: '#0077CC', fontSize: 28 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#1a1a2e' }}>{Constants.APP_LABEL}</Typography>
                        </Box>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button sx={{ textTransform: 'none', color: '#555' }}
                                onClick={() => navigate('/catalog')}>← Каталог</Button>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                    <GroupOutlinedIcon sx={{ color: '#0077CC', fontSize: 28 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Користувачі</Typography>
                    <Chip label={`${total} всього`} size="small" sx={{ ml: 1 }} />
                </Box>

                <Box component="form" onSubmit={e => { e.preventDefault(); setPage(1); setSearch(searchInput); }}
                    sx={{ mb: 2.5, maxWidth: 400 }}>
                    <TextField
                        fullWidth size="small" placeholder="Пошук по прізвищу або email..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
                                endAdornment: searchInput ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }
                        }}
                    />
                </Box>

                {alert && (
                    <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
                        {alert.msg}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Paper elevation={0} sx={{ border: '1px solid #e0e6ed', borderRadius: 3, overflow: 'hidden' }}>
                        {/* Table header — desktop only */}
                        {!isMobile && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr 1fr 1.4fr 80px', gap: 2,
                                px: 2.5, py: 1.5, bgcolor: '#f0f4f8', borderBottom: '1px solid #e0e6ed' }}>
                                {['Ім\'я', 'Email', 'Телефон', 'Адреса доставки', ''].map((h, i) => (
                                    <Typography key={i} sx={{ fontWeight: 600, fontSize: 13, color: '#555' }}>{h}</Typography>
                                ))}
                            </Box>
                        )}

                        {users.length === 0 ? (
                            <Box sx={{ py: 6, textAlign: 'center', color: '#aaa' }}>
                                <Typography>Користувачів не знайдено</Typography>
                            </Box>
                        ) : users.map((user, idx) => (
                            <Box key={user.id}>
                                {editId === user.id ? (
                                    /* ── Edit row ── */
                                    <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2, bgcolor: '#f8fbff', borderLeft: '3px solid #0077CC' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                                                {user.name} {user.last_name}
                                            </Typography>
                                            <Typography sx={{ fontSize: 13, color: '#888' }}>— {user.email}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: '160px 1fr 1fr' },
                                            gap: 1.5, mt: 1.5 }}>
                                            <Box>
                                                <Typography sx={{ fontSize: 12, color: '#888', mb: 0.5 }}>Статус</Typography>
                                                <Select size="small" fullWidth value={editData.status}
                                                    onChange={e => setEditData(p => ({ ...p, status: e.target.value as UserStatus }))}>
                                                    <MenuItem value="REGULAR">REGULAR</MenuItem>
                                                    <MenuItem value="OPT">OPT</MenuItem>
                                                    <MenuItem value="SUPEROPT">SUPEROPT</MenuItem>
                                                </Select>
                                            </Box>
                                            <Box>
                                                <Typography sx={{ fontSize: 12, color: '#888', mb: 0.5 }}>Телефон</Typography>
                                                <TextField size="small" fullWidth value={editData.phone}
                                                    onChange={e => setEditData(p => ({ ...p, phone: e.target.value }))} />
                                            </Box>
                                            <Box>
                                                <Typography sx={{ fontSize: 12, color: '#888', mb: 0.5 }}>Адреса доставки</Typography>
                                                <TextField size="small" fullWidth value={editData.post_address}
                                                    onChange={e => setEditData(p => ({ ...p, post_address: e.target.value }))} />
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
                                            <Button size="small" variant="outlined" startIcon={<CloseOutlinedIcon />}
                                                onClick={cancelEdit} sx={{ textTransform: 'none', borderRadius: 2 }}>
                                                Скасувати
                                            </Button>
                                            <Button size="small" variant="contained" startIcon={saving ? undefined : <SaveOutlinedIcon />}
                                                disabled={saving} onClick={() => saveEdit(user.id)}
                                                sx={{ textTransform: 'none', borderRadius: 2 }}>
                                                {saving ? <CircularProgress size={16} color="inherit" /> : 'Зберегти'}
                                            </Button>
                                        </Box>
                                    </Box>
                                ) : isMobile ? (
                                    /* ── Mobile card ── */
                                    <Box sx={{ px: 2, py: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Box>
                                                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                                                    {user.name} {user.last_name}
                                                </Typography>
                                                <Chip label={user.status} size="small" color={STATUS_COLORS[user.status]}
                                                    sx={{ mt: 0.5, height: 20, fontSize: 11 }} />
                                            </Box>
                                            <Tooltip title="Редагувати">
                                                <IconButton size="small" onClick={() => startEdit(user)} sx={{ color: '#0077CC' }}>
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        <Typography sx={{ fontSize: 13, color: '#444', mt: 0.75, wordBreak: 'break-all' }}>
                                            {user.email}
                                        </Typography>
                                        {user.phone && (
                                            <Typography sx={{ fontSize: 13, color: '#555', mt: 0.25 }}>{user.phone}</Typography>
                                        )}
                                        {user.post_address && (
                                            <Typography sx={{ fontSize: 12, color: '#777', mt: 0.25 }}>{user.post_address}</Typography>
                                        )}
                                    </Box>
                                ) : (
                                    /* ── Desktop row ── */
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr 1fr 1.4fr 80px', gap: 2,
                                        px: 2.5, py: 1.8, alignItems: 'center' }}>
                                        <Box>
                                            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                                                {user.name} {user.last_name}
                                            </Typography>
                                            <Chip label={user.status} size="small" color={STATUS_COLORS[user.status]}
                                                sx={{ mt: 0.5, height: 20, fontSize: 11 }} />
                                        </Box>
                                        <Typography sx={{ fontSize: 13, color: '#444', wordBreak: 'break-all' }}>
                                            {user.email}
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, color: '#555' }}>
                                            {user.phone || <span style={{ color: '#bbb' }}>—</span>}
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, color: '#555', overflow: 'hidden',
                                            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {user.post_address || <span style={{ color: '#bbb' }}>—</span>}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Tooltip title="Редагувати">
                                                <IconButton size="small" onClick={() => startEdit(user)} sx={{ color: '#0077CC' }}>
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                )}
                                {idx < users.length - 1 && <Divider />}
                            </Box>
                        ))}
                    </Paper>
                )}

                {pages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Pagination
                            count={pages} page={page}
                            onChange={(_, v) => { setPage(v); setEditId(null); setSearch(searchInput); }}
                            color="primary" shape="rounded"
                        />
                    </Box>
                )}
            </Container>
        </Box>
    );
};

export default AdminUsersPage;